import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

const replaceVariables = (template: string, variables: any): string => {
  let result = template;

  result = result.replace(/\{\{client_name\}\}/g, variables.client_name || '[Nombre del Cliente]');
  result = result.replace(/\{\{client_id\}\}/g, variables.client_id || '[Documento]');
  result = result.replace(/\{\{client_email\}\}/g, variables.client_email || '[Email]');
  result = result.replace(/\{\{production_days\}\}/g, variables.production_days || '30');

  return result;
};

export const generateServiceAgreementPDF = async (agreement: any) => {
  try {
    console.log('[PDF_GENERATOR] Starting PDF generation for agreement:', agreement);

    let contractText = '';

    try {
      const { data: activeTemplate, error } = await supabase
        .from('service_agreement_templates')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('[PDF_GENERATOR] Error fetching template:', error);
        throw error;
      }

      if (activeTemplate && activeTemplate.content) {
        console.log('[PDF_GENERATOR] Using template from database:', activeTemplate.name);
        contractText = replaceVariables(activeTemplate.content, agreement.variables);
      } else {
        console.warn('[PDF_GENERATOR] No active template found, using default template');
        contractText = replaceVariables(getDefaultTemplate(), agreement.variables);
      }
    } catch (error) {
      console.error('[PDF_GENERATOR] Error loading template, using default:', error);
      contractText = replaceVariables(getDefaultTemplate(), agreement.variables);
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.setFont('helvetica');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ACUERDO DE SERVICIO PRODUCTEX', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha del Contrato: ${agreement.contract_date}`, 15, 30);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    let yPos = 50;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    const lineHeight = 5;

    const lines = doc.splitTextToSize(contractText, 180);

    for (let i = 0; i < lines.length; i++) {
      if (yPos + lineHeight > pageHeight - margin) {
        doc.addPage();
        yPos = 20;
      }

      doc.text(lines[i], margin, yPos);
      yPos += lineHeight;
    }

    const signatureBlockHeight = 60;
    const footerBlockHeight = 35;
    const totalBottomBlockHeight = signatureBlockHeight + footerBlockHeight;

    if (yPos > pageHeight - totalBottomBlockHeight - 10) {
      doc.addPage();
      yPos = 20;
    } else {
      yPos += 20;
    }

    const signatureY = Math.max(yPos, pageHeight - signatureBlockHeight);

    doc.setLineWidth(0.5);
    doc.line(30, signatureY, 110, signatureY);
    doc.line(doc.internal.pageSize.width - 110, signatureY, doc.internal.pageSize.width - 30, signatureY);

    doc.setFontSize(9);
    doc.text(`Cliente: ${agreement.variables.client_name || '___________________'}`, 30, signatureY + 15);
    doc.text('RGX GROUP SRL - Productex', doc.internal.pageSize.width - 110, signatureY + 15);

    const footerY = signatureY + 35;
    doc.setFontSize(8);
    doc.text('Este documento es un acuerdo de servicio generado electrónicamente y tiene validez legal.', 15, footerY);
    doc.text(`Generado el ${new Date().toLocaleDateString('es-AR')} en Ciudad Autónoma de Buenos Aires, Argentina`, 15, footerY + 8);

    const fileName = `Acuerdo_Servicio_${agreement.variables.client_name?.replace(/\s+/g, '_') || 'Cliente'}_${agreement.contract_date}.pdf`;
    doc.save(fileName);

    console.log('[PDF_GENERATOR] PDF generated successfully:', fileName);
  } catch (error) {
    console.error('[PDF_GENERATOR] Error generating PDF:', error);
    throw error;
  }
};

function getDefaultTemplate(): string {
  return `En la Ciudad Autónoma de Buenos Aires, Entre {{client_name}} (en adelante, el "Cliente"), Documento Nacional de Identidad / CUIT / CUIL {{client_id}}, email {{client_email}} y RGX GROUP SRL, CUIT 30-71892824-5 con su marca Productex (en adelante, el "Proveedor"), ambas partes con plena capacidad legal para contratar, deciden suscribir el presente contrato de Servicio conforme el Código Civil y Comercial de la Nación Argentina y las siguientes cláusulas y condiciones:

1. OBJETO DEL ACUERDO - PLAZO

El presente acuerdo tiene por objeto regular los términos y condiciones bajo los cuales el Proveedor realizará la producción de las prendas solicitadas por el Cliente. Los detalles específicos de los artículos, cantidades y montos corresponden a la ficha técnica aprobada por el cliente.

Antes del inicio de la producción, el Proveedor enviará al Cliente una ficha técnica con las especificaciones de cada prenda. El Cliente deberá aprobar dicha ficha antes de iniciar la fabricación. Una vez aprobada, no se permitirán cambios o modificaciones de ningún tipo en la producción.

El presente contrato no supone plazos futuros. Los mismos resultarán SOLO del tracto sucesivo que AMBAS PARTES impartan en la consecución de la relación comercial objeto del presente. Resultando que cada pedido – orden de servicio, resultará consecuente a si misma y se entenderá por finalizada individualmente. Sin que la existencia de una orden de servicio presuponga la concurrencia de otra.

2. PAGOS

El Cliente, previa factura de El Proveedor, se compromete a abonar los montos especificados, tanto el adelanto antes de comenzar la producción, como así también el saldo, antes de la entrega de la misma. Destacando que NO se iniciará la producción sin el pago del adelanto. El saldo deberá ser abonado antes de la entrega de los productos indefectiblemente. Del mismo modo NO se entregará el pedido a El Cliente hasta que éste último haya realizado pago y acreditación efectiva del mismo.

3. PLAZOS DE ENTREGA

Los tiempos de producción estimados serán de {{production_days}} días hábiles, contados desde la fecha de APROBACION DEL PEDIDO Y PAGO DEL ADELANTO, lo que ocurra posterior. No obstante ello, el Cliente entiende y acepta que pueden eventualmente existir demoras debido a factores operativos, disponibilidad de insumos y/o situaciones imprevistas, la cual no podrá ser mayor a 45 días hábiles. El Proveedor notificará al Cliente en caso de cualquier demora significativa dentro de los parámetros fijados. En ningún caso las demoras podrán ser consideradas incumplimiento contractual salvo que resulten responsabilidad directa de El Proveedor.

4. CONTROL DE CALIDAD Y ARTÍCULOS DE SEGUNDA

El Cliente acepta plenamente que hasta un 5% del total de la producción pueda contener artículos de segunda calidad (prendas con detalles que no afectan su utilización o comercialización con o sin descuento). Estos artículos serán facturados al Cliente con un 50% de descuento sobre el precio acordado. En tal sentido – de eventualmente ocurrir lo anteriormente expuesto - El Cliente se compromete a recibir los respectivos productos en plena conformidad. Los artículos con fallas que excedan este 5% no serán entregados ni cobrados al Cliente.

5. PROPIEDAD DE LOS MATERIALES

Para el caso que el Cliente suministrara materiales (telas, insumos, etc.), deberá garantizar ante El Proveedor que resulten suficientes y adecuados para la producción, asumiendo la absoluta responsabilidad ante éste último. Así Productex no será responsable por defectos en la producción derivados de materiales, telas o insumos defectuosos proporcionados por El Cliente, incluso habiéndolos destinado al proceso productivo.

El Proveedor se reserva el derecho de rechazar materiales, en cualquier momento del proceso, que no cumplan con los estándares mínimos de calidad requeridos para la fabricación.

6. MODIFICACIONES EN EL PEDIDO

Cualquier cambio en cantidades, modelos o especificaciones después del inicio de la producción deberá ser aprobado por escrito y podría implicar costos adicionales. Si dichos cambios afectan significativamente los tiempos de producción, el Proveedor notificará al Cliente sobre el nuevo plazo estimado de entrega.

7. CASO FORTUITO - FUERZA MAYOR

El Proveedor no será responsable por demoras o incumplimientos debidos a causas de fuerza mayor, tales como accidentes, huelgas, falta de insumos, problemas climáticos y/o cualquier otro evento fuera del control razonable y de buena fé del Proveedor. Es compromiso del Proveedor mantener informado al Cliente sobre estos eventos. Es responsabilidad del Proveedor resolver con la mayor eficiencia posible los obstáculos que puedan surgir, siempre en virtud del beneficio del Cliente.

8. CONFORMIDAD Y RECEPCIÓN

El Cliente deberá revisar la mercadería al momento de la entrega. Pasadas 48 horas hábiles de la recepción, no se aceptarán reclamos por faltantes y/o defectos en la producción. En caso de disconformidad dentro de este plazo, el Cliente deberá presentar su reclamo por escrito, acompañado de evidencia fotográfica si correspondiera, para su evaluación por parte del Proveedor. Sin que la recepción de material de análisis por parte de El Proveedor suponga aceptación alguna del reclamo en cuestión.

9. JURISDICCION:

Ambas partes, entienden y aceptan que ante cualquier tipo de controversia que pudiera derivarse del presente, se someten a los Tribunales Ordinarios de la Ciudad Autónoma de Buenos Aires, con expresa exclusión de cualquier otro fuero o jurisdicción, y previa lectura y ratificación suscriben 2 (dos) ejemplares de un mismo tenor y a un solo efecto en el lugar y fecha ab – initio indicado.`;
}
